/** Known trading segment ids */
export const SEGMENT_ID = {
  NSEFUT: 1,
  MCXFUT: 2,
  NSEOPT: 3,
  NSECD: 4,
  MXCOPT: 13,
};

export const OPTIONS_SEGMENT_IDS = [SEGMENT_ID.NSEOPT, SEGMENT_ID.MXCOPT];
/** API may return MXCOPT or MCXOPT (same segment 13) */
export const OPTIONS_SEGMENT_LABELS = ["NSEOPT", "MXCOPT", "MCXOPT", "MCXOPTION"];

function normalizeSegmentName(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, "");
}

/** Resolve numeric segment id from id / label / select option / row */
export function getSegmentId(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    if (value.value != null && value.value !== "") {
      return getSegmentId(value.value);
    }
    if (value.segmentId != null && value.segmentId !== "") {
      return getSegmentId(value.segmentId);
    }
    if (value.label != null) return getSegmentId(value.label);
    if (value.name != null) return getSegmentId(value.name);
    if (value.segment != null) return getSegmentId(value.segment);
    return null;
  }
  if (typeof value === "string") {
    const upper = normalizeSegmentName(value);
    if (upper === "NSEOPT") return SEGMENT_ID.NSEOPT;
    if (upper === "MXCOPT" || upper === "MCXOPT" || upper === "MCXOPTION") {
      return SEGMENT_ID.MXCOPT;
    }
    if (upper === "MCXFUT" || upper === "MCX") return SEGMENT_ID.MCXFUT;
    if (upper === "NSEFUT") return SEGMENT_ID.NSEFUT;
    if (upper === "NSECD") return SEGMENT_ID.NSECD;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export function getSegmentLabel(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    return normalizeSegmentName(value.label ?? value.name ?? value.segment ?? "");
  }
  if (typeof value === "string" && Number.isNaN(Number(value))) {
    return normalizeSegmentName(value);
  }
  const id = getSegmentId(value);
  if (id === SEGMENT_ID.NSEOPT) return "NSEOPT";
  if (id === SEGMENT_ID.MXCOPT) return "MXCOPT";
  if (id === SEGMENT_ID.MCXFUT) return "MCXFUT";
  if (id === SEGMENT_ID.NSEFUT) return "NSEFUT";
  if (id === SEGMENT_ID.NSECD) return "NSECD";
  return "";
}

/** NSEOPT + MXCOPT/MCXOPT — options (strike / CE-PE / options symbol APIs) */
export function isOptionsSegment(value) {
  const id = getSegmentId(value);
  if (OPTIONS_SEGMENT_IDS.includes(id)) return true;
  const label = getSegmentLabel(value);
  return OPTIONS_SEGMENT_LABELS.includes(label);
}

/** MCXFUT + MXCOPT/MCXOPT */
export function isMcxExchangeSegment(value) {
  const id = getSegmentId(value);
  return id === SEGMENT_ID.MCXFUT || id === SEGMENT_ID.MXCOPT;
}

/** Segments that use integer lot/qty steps: MCXFUT, NSEOPT, NSECD, MXCOPT */
export function isLotQtyIntegerSegment(value) {
  const id = getSegmentId(value);
  return (
    id === SEGMENT_ID.MCXFUT ||
    id === SEGMENT_ID.NSEOPT ||
    id === SEGMENT_ID.NSECD ||
    id === SEGMENT_ID.MXCOPT
  );
}

/** Brokerage type Fix (vs %): MCXFUT, NSEOPT, MXCOPT */
export function isFixBrokerageSegment(value) {
  const id = getSegmentId(value);
  return (
    id === SEGMENT_ID.MCXFUT ||
    id === SEGMENT_ID.NSEOPT ||
    id === SEGMENT_ID.MXCOPT
  );
}

/** Socket / feed keyword for segment */
export function getSegmentSocketKey(value) {
  const id = getSegmentId(value);
  if (id === SEGMENT_ID.NSEFUT || id === SEGMENT_ID.NSEOPT) return "fonse";
  if (id === SEGMENT_ID.MCXFUT || id === SEGMENT_ID.MXCOPT) return "mcx";
  if (id === SEGMENT_ID.NSECD) return "nsecd";
  return null;
}
